import { useCurrentAccount, useGetBreeds } from '@application/api';
import { useRouter, useShowToast } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { AnimalTypeEnum, DogBreederApplicationStatus } from '@gql/graphql';
import type {
  MtApplicationsOwnModalActionType,
  MtApplicationsOwnModalFormValues,
  SelectOption,
} from '@presentation/components';
import { MtApplicationsOwnModal } from '@presentation/components';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useApplicationInitialValuesBuilder } from './api/useApplicationInitialValuesBuilder';
import { useGetApplication } from './api/useGetApplication';
import { useOwnApplication } from './api/useOwnApplication';
import { useValidateForm } from './api/useValidateForm';
import { useWaitlistApplication } from './api/useWaitlistApplication';
import type { MtApplicationsOwnModalControllerProps } from './types';

export * from './types';

export const MtApplicationsOwnModalController: FC<MtApplicationsOwnModalControllerProps> = (
  props,
) => {
  const { applicationId, breeder, modal, onRedirect } = props;
  const { navigateToRoute } = useRouter();
  const { application, applicationLoading } = useGetApplication(applicationId);
  const { currentAccountType } = useCurrentAccount();
  const { validate } = useValidateForm();
  const { applicationInitialValuesBuilder } = useApplicationInitialValuesBuilder();

  const { ownApplication, ownApplicationLoading } = useOwnApplication();
  const { waitlistApplication, waitlistApplicationLoading } = useWaitlistApplication();
  const isLoading = ownApplicationLoading || waitlistApplicationLoading;
  const { translate } = useRouter();
  const { showToast } = useShowToast();
  const { breedOptions } = useGetBreeds();

  const breedSelectOptions = useMemo((): SelectOption<number>[] => {
    const breedIdsBreeder = (breeder?.breederBreeds ?? []).map((breed) => breed.dogBreedId);
    return breedOptions.filter((breed) => breedIdsBreeder.includes(breed.value));
  }, [breedOptions, breeder?.breederBreeds]);

  const actionOptions: SelectOption<MtApplicationsOwnModalActionType>[] = [
    {
      label: translate('common.modals.own.ownAction'),
      value: 'own',
    },
    {
      label: translate('common.modals.own.waitlistAction'),
      value: 'waitlist',
    },
  ];

  const showActionsField =
    currentAccountType === 'breeder' &&
    application?.status !== DogBreederApplicationStatus.WAITING_LIST;
  const ownModalForm = useFormik<MtApplicationsOwnModalFormValues>({
    enableReinitialize: true,
    initialValues: applicationInitialValuesBuilder(application),
    validate: (values) => validate(currentAccountType, values),
    onSubmit: async (values) => {
      if (!applicationId) throw new Error();
      const { action } = values;
      if (action === 'own') {
        await ownApplication({
          applicationId,
          ...(values.name ? { name: values.name } : {}),
          breedId: values.breed!,
          dateOfBirth: values.birthday! as any,
          gender: values.gender!,
        });
        if (modal.onCloseClick) {
          modal.onCloseClick();
        }
        Analytics.sendAnimalCreatedEvent(AnimalTypeEnum.DOG, 'adoption');
        showToast(translate('common.toasts.owned'), { type: 'success' });
        setTimeout(() => {
          onRedirect();
        }, 500);
      } else {
        await waitlistApplication({
          applicationId,
          breedId: values.breed!,
          ...(values.name ? { name: values.name } : {}),
          ...((values.birthday ? { dateOfBirth: values.birthday } : {}) as any),
          ...(values.gender ? { gender: values.gender } : {}),
        });
        if (modal.onCloseClick) {
          modal.onCloseClick();
        }
        showToast(translate('common.toasts.waitlisted'), { type: 'success' });
        setTimeout(() => {
          navigateToRoute('mtWaitingList');
        }, 500);
      }
    },
  });

  return (
    <FormikProvider value={ownModalForm}>
      <MtApplicationsOwnModal
        modal={modal}
        formOptions={{
          breedIds: breedSelectOptions,
          showNameField: currentAccountType === 'buyer',
          actions: actionOptions,
          showActionsField,
        }}
        isLoading={isLoading}
      />
    </FormikProvider>
  );
};
